import { blobEndpoint, datasetsContainer, storageAccount } from '~lib/storage/config';
import {
  buildBlobReadSasUrl,
  parseUserDelegationKeyXml,
  toIsoSeconds,
  type UserDelegationKey,
} from '~lib/storage/blob-sas';

const ABS_API_VERSION = '2024-08-04';

// Azure caps a user delegation key — and therefore any SAS signed with it —
// at seven days.
export const MAX_TEMPORARY_LINK_MS = 7 * 24 * 60 * 60 * 1000;

const MIN_TEMPORARY_LINK_MS = 60 * 1000;

// Clock-skew allowance. The key's start is back-dated by this much so a
// browser clock running ahead of Azure's can't yield a link that is "not yet
// valid" for its first minutes; the longest expiry is pulled in by the same
// margin so a 7-day request never trips the 7-day ceiling.
const CLOCK_SKEW_MS = 5 * 60 * 1000;

export interface TemporaryLink {
  url: string;
  expiresAt: Date;
}

// Mints a read-only, HTTPS-only URL for one blob in the datasets container,
// valid for roughly `ttlMs` from now. One POST for a user delegation key,
// then a local HMAC. Nothing is stored on the Azure side — the key is
// derived, not created — so calling this repeatedly is harmless.
export async function createTemporaryLink(
  token: string,
  blobName: string,
  ttlMs: number,
): Promise<TemporaryLink> {
  const now = Date.now();
  const ttl = Math.min(
    Math.max(ttlMs, MIN_TEMPORARY_LINK_MS),
    MAX_TEMPORARY_LINK_MS - CLOCK_SKEW_MS,
  );
  const startsOn = new Date(now - CLOCK_SKEW_MS);
  const expiresOn = new Date(now + ttl);

  const key = await getUserDelegationKey(token, startsOn, expiresOn);
  const url = await buildBlobReadSasUrl({
    blobEndpoint,
    account: storageAccount,
    container: datasetsContainer,
    blobName,
    key,
    expiresOn,
  });
  return { url, expiresAt: expiresOn };
}

// `Get User Delegation Key`. This is an account-level operation: the caller
// needs the `generateUserDelegationKey` action on the storage account, which
// the container-scoped data role does not confer — hence the Storage Blob
// Delegator assignment in the README.
export async function getUserDelegationKey(
  token: string,
  startsOn: Date,
  expiresOn: Date,
): Promise<UserDelegationKey> {
  const url = new URL(`${blobEndpoint}/`);
  url.searchParams.set('restype', 'service');
  url.searchParams.set('comp', 'userdelegationkey');

  const body =
    '<?xml version="1.0" encoding="utf-8"?>' +
    `<KeyInfo><Start>${toIsoSeconds(startsOn)}</Start><Expiry>${toIsoSeconds(expiresOn)}</Expiry></KeyInfo>`;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-ms-version': ABS_API_VERSION,
        'Content-Type': 'application/xml',
      },
      body,
    });
  } catch (error) {
    // fetch rejects only on network-level failure, which for a cross-origin
    // POST almost always means the preflight was refused. Reads and uploads
    // never POST, so the CORS rule is the first thing to check.
    throw new Error(
      'Could not reach the storage account. If downloads work but this does not, the Blob service CORS rule is probably missing POST for this origin.',
      { cause: error },
    );
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    const code = /<Code>([^<]+)<\/Code>/.exec(text)?.[1];
    const detail = `${response.status}${code ? ` ${code}` : ''}`;
    if (response.status === 403) {
      throw new Error(
        `Storage refused to issue a delegation key (${detail}). Creating links needs the Storage Blob Delegator role on the storage account itself; the container-scoped data role is not enough.`,
      );
    }
    throw new Error(
      `Get User Delegation Key failed: ${detail}${text ? ` — ${text.slice(0, 200)}` : ''}`,
    );
  }

  return parseUserDelegationKeyXml(await response.text());
}
